import { type FieldValues, useFormContext } from 'react-hook-form';
import { FilterBadgesList } from '@/components/FilterBadgesList';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { changeOrderStatusOptions } from '@/features/ChangeOrder/Summary/constants';
import type { ReactSelectOption } from '@/types/Constants';
import { isObjectEmpty } from '@/utils';

interface FilterData {
  phase?: ReactSelectOption;
  submitter?: ReactSelectOption;
  status?: ReactSelectOption;
  approver?: ReactSelectOption;
  minImpact?: number;
  maxImpact?: number;
}

interface IAddTDMFiltersFormProps {
  displayFilters: any;
  submitterOptions: ReactSelectOption[];
  approverOptions: ReactSelectOption[];
  handleAppliedFilters: (filters: any) => void;
  handleDisplayFilters: (filters: any) => void;
  hide: () => void;
}

const fieldLabels: Record<keyof FilterData, string> = {
  phase: 'Phase',
  submitter: 'Submitter',
  status: 'Status',
  approver: 'Approver',
  minImpact: 'Min Impact ($)',
  maxImpact: 'Max Impact ($)',
};

const FiltersHookForm = ({
  hide,
  submitterOptions,
  approverOptions,
  handleAppliedFilters,
  handleDisplayFilters,
}: IAddTDMFiltersFormProps) => {
  const formMethods = useFormContext<FilterData>();
  const formValues = formMethods.watch();

  const onSubmit = (formData: FilterData) => {
    const appliedFilters: FieldValues = {
      phaseSlug: formData.phase?.value,
      minImpact: formData.minImpact,
      maxImpact: formData.maxImpact,
      submitterId: formData.submitter?.value,
      approverId: formData.approver?.value,
      status: formData.status?.value,
    };

    const displayFilters = formData;

    handleAppliedFilters(appliedFilters);
    handleDisplayFilters(displayFilters);
    hide();
  };

  const getDisplayFilters = () => {
    return Object.fromEntries(
      Object.entries(formValues)
        .filter(([_, value]) => !!value)
        .map(([key, value]) => [
          key,
          typeof value !== 'object'
            ? {
                label: fieldLabels[key as keyof FilterData],
                value: value,
              }
            : {
                label: fieldLabels[key as keyof FilterData],
                value: value.label,
              },
        ])
    );
  };

  const displayFilters = getDisplayFilters();

  return (
    <HookForm onSubmit={onSubmit}>
      <div className='p-6'>
        <h2 className='mt-0 capitalize'>Filter By</h2>
        {!isObjectEmpty(displayFilters) && (
          <FilterBadgesList
            className='pb-4'
            displayFilters={displayFilters}
            handleRemoveFilter={(key) => {
              formMethods.setValue(key as keyof FilterData, '' as any);
            }}
          />
        )}
        <section className='grid grid-cols-2 gap-5 items-center'>
          <HookInput formMethods={formMethods} label={fieldLabels.minImpact} type='number' name='minImpact' />
          <HookInput formMethods={formMethods} label={fieldLabels.maxImpact} type='number' name='maxImpact' />
          <HookReactSingleSelect formMethods={formMethods} label='Submitter' options={submitterOptions} placeholder='' name='submitter' />
          <HookReactSingleSelect formMethods={formMethods} label='Approver' options={approverOptions} placeholder='' name='approver' />
          <HookReactSingleSelect
            formMethods={formMethods}
            componentClassName='col-span-2'
            label='Status'
            options={changeOrderStatusOptions}
            placeholder=''
            name='status'
          />
        </section>
      </div>

      <div className='flex gap-2 justify-end p-6 border-t border-gray-300'>
        <Button label='Cancel' btnStyle='neutral' onClick={hide} />
        <Button label='Apply' btnStyle='primary' btnType='submit' />
      </div>
    </HookForm>
  );
};

const FiltersForm = (props: IAddTDMFiltersFormProps) => {
  return (
    <Modal customStyle='max-w-xl p-0'>
      <HookFormProvider defaultValues={props.displayFilters}>
        <FiltersHookForm {...props} />
      </HookFormProvider>
    </Modal>
  );
};

export default FiltersForm;
